import { Component, OnInit, Input, OnDestroy } from "@angular/core";
import { IContaCerta, IContaCertaConsumptions } from "./edp-conta-certa-modal.model";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { EdpModalService } from "@edp-components/edp-modal";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import moment, { Moment } from "moment";
import { CategoryAxis, ValueAxis } from "@amcharts/amcharts4/charts";
import { Container } from "@amcharts/amcharts4/core";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { getConsumptions } from "@edp-store/selectors/consumption.selectors";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { getSimulation, getBalance } from "@edp-store/selectors/dashboard.selectors";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Contract } from "@edp-components/edp-contract";
import { Simulation } from "@edp-components/edp-card-widgets/edp-widget-consumptions/edp-consumptions.models";
import { IConsumptions } from "@edp-components/edp-card-widgets/edp-widget-conta-certa/edp-conta-certa.model";
import { ContaCertaService } from "@edp-services/conta-certa.service";
import { EdpResponseBody } from "@edp-models/response.model";
import { EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { OperationHistoryService } from "@edp-services/operation-history.service";
import { IInvoicesChart } from "@edp-models/invoices.model";
import { LanguageTypeEnum } from "@edp-enums/language.enum";
import { LanguageService } from "@edp-services/language.service";

am4core.options.autoSetClassName = true;
am4core.useTheme(am4themes_animated);
am4core.addLicense("CH120763846");

@Component({
    selector: "edp-conta-certa",
    templateUrl: "./edp-conta-certa-modal.component.html",
    styleUrls: ["./edp-conta-certa-modal.component.scss"]
})
export class EdpContaCertaModalComponent implements OnInit, OnDestroy {

    static TOP_CORNER_RADIUS = 20;
    static BOTTOM_CORNER_RADIUS = 20;

    @Input() public data: IModalData;

    private contract: Contract;
    private simulation: Simulation;
    private consumptions: IConsumptions;
    private chart: am4charts.XYChart;
    private unsubscribe = new Subject();
    private args = {
        contractId: null,
        budgetBillingValue: null
    };

    public contaCertaInfo: IContaCerta = {
        monthlyAverage: 0,
        monthlyPayment: 0,
        lastMonthPayment: 0,
        sugestedMonthlyPayment: 0,
        consumptions: [],
        beginMonth: "",
        beginYear: "",
        endMonth: "",
        endYear: "",
    };
    public payOrReceive: string;
    public lastMonthPaymentBasedOnChoice: number;
    public multipleListValues = [];
    public monthlyPaymentForm: FormGroup;
    public showInput = false;
    public buttonMessage: string;
    public buttonDisabled = true;
    public highLightedValue: number;
    public beginMonth: string;
    public beginYear: string;
    public endMonth: string;
    public endYear: string;
    public loading: boolean;
    public payOrReceive4LastPayment: number;
    public floatableValue: string;
    public balance: {
        invoices?: any;
        dateFrom?: string;
        dateTo?: string;
        balance?: number;
        debtBalance?: number;
        movements?: any[];
        invoicesChart?: IInvoicesChart[];
    };
    public postingDate: Moment;
    public messageShow = false;
    public dateToShow: string;
    public min: number;
    public max: number;
    private range = {
        min: 0.85, // -15%
        max: 1.99 // +99%
    };

    constructor(
        private translate: TranslateService,
        private edpModalService: EdpModalService,
        private store: Store<IAppState>,
        private fb: FormBuilder,
        private contaCertaService: ContaCertaService,
        private toaster: EdpToasterService,
        public languageService: LanguageService,
        private operationHistoryService: OperationHistoryService) {

        this.store.pipe(
            takeUntil(this.unsubscribe),
            select(getContract)
        ).subscribe(contract => {
            this.contract = contract;
        });

        this.store.pipe(
            takeUntil(this.unsubscribe),
            select(getSimulation)
        ).subscribe(simulation => {
            this.simulation = simulation;
            if (!this.simulation.simulationValue) {
                this.toaster.create(this.translate.instant("pt.edp.edponline.endpoint.error"), EdpToastType.error).show();
            }
        });

        this.store.pipe(
            takeUntil(this.unsubscribe),
            select(getConsumptions)
        ).subscribe(consumptions => {
            this.consumptions = consumptions;
            this.extractInfoFromState(this.consumptions);
        });

        this.store.pipe(
            takeUntil(this.unsubscribe),
            select(getBalance)
        ).subscribe(balance => {
            this.balance = balance;
            if (this.balance.invoices.length > 0) {
                this.postingDate = moment(this.balance.invoices[0].postingDate);
                this.postingDate.subtract(20, "days");
                this.postingDate.add(1, "month");
                this.messageShow = this.postingDate <= moment();
                this.dateToShow = moment().add(1, "month").locale(this.languageService.getLocale()).format("MMMM");
            }
        });
    }

    ngOnInit() {
        this.buildChart();
        this.calculateIntervalValues();
        this.setPayOrReceive();
        this.logOperation("VIEW_CONTA_CERTA");
        this.monthlyPaymentForm = this.fb.group({
            newContaCertaValue: ["", [Validators.min(this.min), Validators.pattern("^\\d*$"), Validators.max(this.max)]],
        });
    }

    public closeModal(_action: boolean): void {
        this.edpModalService.close(false);
    }

    public useSugestedValue(itemValue: number): void {
        this.showInput = false;
        this.monthlyPaymentForm.reset();
        this.setPayOrReceive(itemValue);
        this.highLightedValue = itemValue;
    }

    public useOtherValue(): void {
        this.showInput = true;
        this.setButtonMessage();
        this.highLightedValue = -1;
    }

    public isInvalid() {
        return !this.monthlyPaymentForm.valid;
    }

    public submitNewValue() {

        this.loading = true;
        this.buttonDisabled = true;

        if (this.monthlyPaymentForm.valid) {
            const aux: number = this.monthlyPaymentForm.value.newContaCertaValue || this.highLightedValue;
            this.args = {
                budgetBillingValue: aux.toFixed(2), // mandatory ..else...the service doest work
                contractId: this.contract.ContractId
            };

            this.contaCertaService.updateContaCertaValue(this.args).subscribe((resp: EdpResponseBody) => {
                this.toaster.create(
                    this.translate.instant("pt.edp.edponline.conta.certa.label.send.success"), EdpToastType.info
                ).show();
                this.edpModalService.close(false);
                this.loading = false;
                this.buttonDisabled = false;
            }, (_response: Error) => {
                this.loading = false;
                this.buttonDisabled = false;
            });
        } else {
            this.loading = false;
            this.buttonDisabled = false;
        }
    }

    private setButtonMessage(valueForButton?: number): void {
        if (valueForButton && (valueForButton % 1) === 0) {
            this.buttonMessage = this.translate.instant("pt.edp.edponline.conta.certa.label.submit.x.value", { value: valueForButton });
            this.buttonDisabled = false;
        } else {
            this.buttonMessage = this.translate.instant("pt.edp.edponline.conta.certa.label.submit.monthly.payment");
            this.buttonDisabled = true;
        }
    }

    private setPayOrReceive(newMonthlyPayment?: number): void {


        const aux = this.messageShow ? this.calculateValueBasedOnChoiceCC(
            this.simulation.simulationValue,
            newMonthlyPayment || this.contaCertaInfo.sugestedMonthlyPayment,
            parseInt(this.contract.ContaCertaValue, 0)
        ) : this.calculateValueBasedOnChoice(
            this.simulation.simulationValue,
            newMonthlyPayment || this.contaCertaInfo.sugestedMonthlyPayment
        );
        this.payOrReceive = (aux >= 0) ?
            this.translate.instant("pt.edp.edponline.endpoint.conta.certa.label.twelve.month.title.pay") : this.translate.instant("pt.edp.edponline.endpoint.conta.certa.label.twelve.month.title.recieve");
        this.lastMonthPaymentBasedOnChoice = Math.abs(Math.round(aux));
        this.setButtonMessage(newMonthlyPayment);
    }

    private setLastMonthPayment(simValue: string, monthlyPayment: number): number {

        const aux = this.messageShow ? this.calculateValueBasedOnChoiceCC(simValue,
            monthlyPayment,
            parseInt(this.contract.ContaCertaValue, 0)
        ) : this.calculateValueBasedOnChoice(simValue, monthlyPayment);
        this.payOrReceive4LastPayment = (aux >= 0) ?
            this.translate.instant("pt.edp.edponline.endpoint.conta.certa.label.twelve.month.title.pay") : this.translate.instant("pt.edp.edponline.endpoint.conta.certa.label.twelve.month.title.recieve");
        return Math.abs(Math.round(aux));
    }

    private calculateIntervalValues(): void {
        this.nearestMultiples(this.contaCertaInfo.sugestedMonthlyPayment);
    }

    private extractInfoFromState(consumptions: any): void {
        const auxContaCertaValue = (this.contract.ContaCertaValue) ? parseInt(this.contract.ContaCertaValue, 0) : 0;

        this.beginMonth = moment(consumptions.startContaCerta).locale(this.languageService.getLocale()).format("MMM");
        this.beginYear = moment(consumptions.startContaCerta).format("YYYY");
        this.endMonth = moment(consumptions.endContaCerta).locale(this.languageService.getLocale()).format("MMM");
        this.endYear = moment(consumptions.endContaCerta).format("YYYY");
        this.contaCertaInfo = {
            beginMonth: moment(consumptions.startContaCerta).locale(this.languageService.getLocale()).format("MMM"),
            beginYear: moment(consumptions.startContaCerta).format("YYYY"),
            endMonth: moment(consumptions.endContaCerta).locale(this.languageService.getLocale()).format("MMM"),
            endYear: moment(consumptions.endContaCerta).format("YYYY"),
            monthlyAverage: Math.round(consumptions.averageContaCerta),
            monthlyPayment: auxContaCertaValue,
            lastMonthPayment: this.setLastMonthPayment(this.simulation.simulationValue, auxContaCertaValue),
            sugestedMonthlyPayment: Math.round(consumptions.averageContaCerta),
            consumptions: consumptions.consumptionsContaCerta.map((value: {
                totalEurosE: any;
                totalEurosG: any;
                startPeriod: moment.MomentInput;
                totalEuros: any;
            }) => {
                return {
                    electricity: value.totalEurosE || 0,
                    gas: value.totalEurosG || 0,
                    taxes: 0,
                    month: moment(value.startPeriod).locale(this.languageService.getLocale()).format("MMM").toUpperCase(),
                    totalEuros: value.totalEuros
                } as IContaCertaConsumptions;
            })
        };

        this.min = Math.round(this.contaCertaInfo.monthlyPayment * this.range.min);
        this.max = Math.round(this.contaCertaInfo.monthlyPayment * this.range.max);


        this.highLightedValue = this.contaCertaInfo.sugestedMonthlyPayment;
        this.floatableValue = (this.contaCertaInfo.monthlyPayment > this.contaCertaInfo.sugestedMonthlyPayment) ?
            this.translate.instant("pt.edp.edponline.conta.certa.label.floatableValue.reduction") : this.translate.instant("pt.edp.edponline.conta.certa.label.floatableValue.raise");
    }

    private calculateValueBasedOnChoice(simValue: string, monthlyPayment: number): number {
        const missingMonths: number = moment(this.consumptions.endContaCerta).subtract(1, "months").diff(moment(), "months");
        const rst = parseFloat(simValue) - (monthlyPayment * missingMonths);
        return isNaN(rst) ? 0 : rst;
    }

    private calculateValueBasedOnChoiceCC(simValue: string, monthlyPayment: number, newMonthlyPayment: number): number {
        const missingMonths: number = moment(this.consumptions.endContaCerta).subtract(1, "months").diff(moment(), "months");
        const rst = parseFloat(simValue) - monthlyPayment - (missingMonths - 1) * newMonthlyPayment;
        return isNaN(rst) ? 0 : rst;
    }

    /**
     * Nearests multiples - Calculate X numbers multiple of value received
     * @param value - the number from which we calculate the multiples
     * @param [multiple] - the multiple number
     * @param [numbersBelow] - the number of multiples values to calculate BELOW value received
     * @param [total] - total number of multiples to calculate
     */
    private nearestMultiples(value: number, multiple = 5, numbersBelow = 2, total = 4): void {
        const valueMod = (value % multiple);
        let multipleValueFound = (value - valueMod - multiple * numbersBelow) + valueMod;

        while (multipleValueFound < 0) {
            multipleValueFound = multipleValueFound + multiple;
        }

        for (let i = 0; i < total; i++) {
            this.multipleListValues.push(multipleValueFound);
            multipleValueFound = multipleValueFound + multiple;
        }
    }

    private buildChart(): void {
        // Create chart instance
        this.chart = am4core.create("conta-certa-chart", am4charts.XYChart);
        this.chart.paddingTop = 40;

        // Add data
        this.chart.data = this.contaCertaInfo.consumptions;

        // Create axis
        this.setCategoryAxis();
        const valueAxis = this.setValueAxis();

        // Create series
        if (this.contract.HasE) {
            this.createSeries("electricity",
                this.translate.instant("pt.edp.edponline.edpcontractconsumptionselectricity.contractconsumptionselectricity.eletricidade"),
                true,
                "#263CC8"
            );
        }

        if (this.contract.HasG) {
            this.createSeries("gas",
                this.translate.instant("pt.edp.edponline.edpcontractconsumptionsgas.contractconsumptionsgas.gas"),
                true,
                "#A784FF"
            );
        }

        // Para quando o BE enviar as taxas
        // this.createSeries("taxes", "Taxas e Impostos", true, "#f3d5d7");

        // Create legend
        this.setLegend();

        // Create Average Consumption range
        this.setAverageRange(valueAxis);

        // Create Conta Certa range
        this.setContaCertaRange(valueAxis);

    }

    // Average Consumption range
    private setAverageRange(valueAxis: ValueAxis): void {
        const rangeAC = valueAxis.axisRanges.create();
        rangeAC.value = this.contaCertaInfo.monthlyAverage;
        rangeAC.grid.stroke = am4core.color("#225E66");
        rangeAC.grid.strokeWidth = 1.5;
        rangeAC.grid.strokeOpacity = 1;
        rangeAC.label.text = this.translate.instant("pt.edp.edponline.endpoint.conta.certa.chart.legend.average") +
            " [bold]" + rangeAC.value + " €[/]";
        rangeAC.label.text.fontsize(12);
        rangeAC.label.inside = true;
        rangeAC.label.align = "right";
        rangeAC.label.verticalCenter = "bottom";
        rangeAC.label.fill = am4core.color("#225E66");
    }

    // Conta Certa range
    private setContaCertaRange(valueAxis: ValueAxis): void {
        const rangeCC = valueAxis.axisRanges.create();
        rangeCC.value = this.contaCertaInfo.monthlyPayment;
        rangeCC.grid.stroke = am4core.color("#225E66");
        rangeCC.grid.strokeWidth = 1;
        rangeCC.grid.strokeOpacity = 1;
        rangeCC.grid.strokeDasharray = "6";
        rangeCC.label.text = this.translate.instant("pt.edp.edponline.endpoint.conta.certa.title") + " [bold]" + rangeCC.value + " €[/]";
        rangeCC.label.text.fontsize(12);
        rangeCC.label.inside = true;
        rangeCC.label.align = "right";
        rangeCC.label.verticalCenter = "bottom";
        rangeCC.label.fill = am4core.color("#225E66");
    }
    // Series
    private createSeries(field: string, name: string, stacked: boolean, color: string | am4core.Color | am4core.iRGB): void {
        const series = this.chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = field;
        series.dataFields.categoryX = "month";
        series.name = name;
        series.stacked = stacked;
        series.columns.template.fill = am4core.color(color);
        series.columns.template.width = 10;
        series.columns.template.column.adapter.add("cornerRadiusTopLeft", this.topCornerRadius.bind(this));
        series.columns.template.column.adapter.add("cornerRadiusTopRight", this.topCornerRadius.bind(this));
        series.columns.template.column.adapter.add("cornerRadiusBottomLeft", this.bottomCornerRadius.bind(this));
        series.columns.template.column.adapter.add("cornerRadiusBottomRight", this.bottomCornerRadius.bind(this));
        series.columns.template.strokeOpacity = 0;
    }

    // Create Legend
    private setLegend(): void {
        // Add legend
        this.chart.legend = new am4charts.Legend();
        this.chart.legend.position = "top";
        this.chart.legend.contentAlign = "right";
        // Disable marker click
        this.chart.legend.itemContainers.template.clickable = false;
        this.chart.legend.itemContainers.template.focusable = false;
        this.chart.legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;
        // End Disable marker click

        // Legend marker customization
        const markerTemplate = this.chart.legend.markers.template;
        markerTemplate.width = 8;
        markerTemplate.height = 8;

        this.chart.legend.clickable = false;

        // Move legend to top container (above chart)
        const legendContainer = this.setLegendContainer();
        this.chart.legend.parent = legendContainer;

        const chartTitle = legendContainer.createChild(am4core.Label);
        chartTitle.text = this.translate.instant("pt.edp.edponline.conta.certa.label.consumption.evolution");
        chartTitle.x = 35;
        chartTitle.y = 8;

        const chartSubtitle = legendContainer.createChild(am4core.Label);
        chartSubtitle.text = this.translate.instant("pt.edp.edponline.conta.certa.label.consumption.evolution.months",
            {
                beginMonth: this.beginMonth,
                beginYear: this.beginYear,
                endMonth: this.endMonth,
                endYear: this.endYear,
            });
        chartSubtitle.fill = am4core.color("#90979F");
        chartSubtitle.x = 35;
        chartSubtitle.y = 24;
    }

    // Legend Container
    private setLegendContainer(): Container {
        const legendContainer = am4core.create("conta-certa-legend", am4core.Container);
        legendContainer.width = am4core.percent(100);
        legendContainer.height = am4core.percent(100);

        return legendContainer;
    }

    // Category Axis
    private setCategoryAxis(): CategoryAxis {
        const categoryAxis = this.chart.xAxes.push(new CategoryAxis());
        categoryAxis.dataFields.category = "month";
        categoryAxis.renderer.minGridDistance = 20;
        categoryAxis.renderer.grid.template.disabled = true;
        categoryAxis.renderer.labels.template.fill = am4core.color("#455558");
        categoryAxis.renderer.grid.template.location = 0;

        return categoryAxis;
    }

    // Value Axis
    private setValueAxis(): ValueAxis {
        const valueAxis = this.chart.yAxes.push(new ValueAxis());

        let lastValue = 0;
        let minValue = 0;
        this.chart.data.map((data) => {
            if (data.totalEuros > lastValue) {
                lastValue = data.totalEuros;
            }
            if (data.totalEuros < minValue) {
                minValue = data.totalEuros;
            }
            if (lastValue < this.contaCertaInfo.monthlyPayment) {
                lastValue = this.contaCertaInfo.monthlyPayment;
            }
        });

        valueAxis.max = lastValue * 1.25; // some margin to avoid column stick to top
        valueAxis.min = minValue;
        valueAxis.layout = "absolute";
        valueAxis.paddingLeft = 10;
        valueAxis.paddingRight = 10;

        valueAxis.renderer.grid.template.stroke = am4core.color("#BECACC");
        valueAxis.renderer.labels.template.fill = am4core.color("#BECACC");

        valueAxis.title.text = "€";
        valueAxis.title.rotation = 0;
        valueAxis.title.align = "center";
        valueAxis.title.valign = "top";
        valueAxis.title.dy = -40;
        valueAxis.title.dx = -4;
        valueAxis.title.fill = am4core.color("#90979F");

        return valueAxis;

    }

    // Adapter to set topCornerRadius on serie that goes on top
    private topCornerRadius(radius: number, item: { dataItem: any; }): number {
        const dataItem = item.dataItem;

        // Find the last series in this stack
        let lastSeries: am4charts.XYSeries;
        this.chart.series.each(series => {
            if (dataItem.dataContext[series.dataFields.valueY] && !series.isHidden && !series.isHiding) {
                lastSeries = series;
            }
        });

        // If current series is the one, use rounded corner
        return dataItem.component === lastSeries ? EdpContaCertaModalComponent.TOP_CORNER_RADIUS : radius;
    }

    // Adapter to set bottomCornerRadius on serie that goes on bottom
    private bottomCornerRadius(radius: number, item: { dataItem: any; }): number {
        const dataItem = item.dataItem;

        // Find the frist series in this stack
        let firstSerie: am4charts.XYSeries;
        const serie = this.chart.series.getIndex(0);
        if (dataItem.dataContext[serie.dataFields.valueY] && !serie.isHidden && !serie.isHiding) {
            firstSerie = serie;
        }

        // If current series is the one, use rounded corner
        return dataItem.component === firstSerie ? EdpContaCertaModalComponent.BOTTOM_CORNER_RADIUS : radius;
    }

    public ngOnDestroy() {
        if (this.chart) {
            this.chart.dispose();
        }
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    private logOperation(op: string) {
        this.operationHistoryService.registerWithContract(
            this.contract.ContractId,
            op,
            null
        );
    }
}
